import React from "react";
import { graphql, useLazyLoadQuery } from "react-relay";

import { HubspotAccount, HubspotContact, HubspotDeal, HubspotTicket } from "~/stream/SideBar/CRMPanel/Hubspot";

import type { HubspotCardQuery, HubspotCardQuery$data } from "~/__generated__/HubspotCardQuery.graphql";

import styles from "./CompanyPanel.module.css";

const hubspotCardQuery = graphql`
	query HubspotCardQuery($emails: [String!]!) {
		hubspotContactsForEmails(emails: $emails) {
			__typename
			id
			url
			name
			email
			title
			company {
				id
				url
				name
			}
			deals {
				id
				url
				amount
				name
			}
			tickets {
				id
				url
				subject
				description
			}
		}
	}
`;

type HubspotContactNode = NonNullable<HubspotCardQuery$data["hubspotContactsForEmails"]>[number];

export function HubspotCard({ emails }: { emails: string[] }) {
	const { hubspotContactsForEmails } = useLazyLoadQuery<HubspotCardQuery>(
		hubspotCardQuery,
		{ emails },
		{ fetchPolicy: "store-and-network" },
	);

	const contacts: HubspotContactNode[] = [];
	const companies: NonNullable<HubspotContactNode["company"]>[] = [];
	const deals: HubspotContactNode["deals"][number][] = [];
	const tickets: HubspotContactNode["tickets"][number][] = [];

	hubspotContactsForEmails?.forEach(hubspotObject => {
		contacts.push(hubspotObject);
		if (hubspotObject.company && !companies.some(company => company.id === hubspotObject.company?.id)) {
			companies.push(hubspotObject.company);
		}
		if (hubspotObject.deals) {
			hubspotObject.deals.forEach(deal => {
				if (!deals.some(d => d.id === deal.id)) {
					deals.push(deal);
				}
			});
		}
		if (hubspotObject.tickets) {
			hubspotObject.tickets.forEach(ticket => {
				if (!tickets.some(t => t.id === ticket.id)) {
					tickets.push(ticket);
				}
			});
		}
	});

	return (
		<div className={styles.hubspotCard}>
			{contacts.length > 0 ? (
				contacts.map(contact => <HubspotContact key={contact.id} contact={contact} />)
			) : (
				<p>No Hubspot contacts found</p>
			)}
			{companies.map(company => (
				<HubspotAccount key={company.id} company={company} />
			))}
			{deals.map(deal => (
				<HubspotDeal key={deal.id} deal={deal} />
			))}
			{tickets.map(ticket => (
				<HubspotTicket key={ticket.id} ticket={ticket} />
			))}
		</div>
	);
}
